import { useEffect, useState } from "react";
import moment from "moment";
import { getEnum } from "../components/form/Helpers";
import { GetEntityRecordById } from "./service";
import {
  BadgeList,
  StatusBadge,
} from "../components/elements/_CommonElements";
import { BackgroundStyle, SidebarItemType } from "./enums";
import TextArea from "../components/elements/TextArea";

const currencyFormat = new Intl.NumberFormat("en-GB", {
  style: "currency",
  currency: "GBP",
});

const numberFormat = new Intl.NumberFormat("en-GB");

const priceRegex = /^\d+(\.\d{1,2})?$/;

const checkDateTimeIsAfterToday = (date) => {
  if (date) {
    return moment(date).isAfter();
  }
  return false;
};

const checkIfFixturesAndSeriesEnabled = (globalState) => {
  return (
    globalState &&
    globalState.config &&
    globalState.config["FixturesAndSeries"]
  );
};

const checkIfEmailMarketingEnabled = (globalState) => {
  return (
    globalState &&
    globalState.config &&
    globalState.config["EmailMarketingEnabled"]
  );
};

const checkIfDynamicContentEnabled = (globalState) => {
  return (
    globalState &&
    globalState.config &&
    globalState.config["DynamicContentEnabled"]
  );
};

const checkIfWarehouseCommunicationAudienceEnabled = (
  globalState
) => {
  return (
    globalState &&
    globalState.config &&
    globalState.config["WarehouseCommunicationAudienceEnabled"]
  );
};

const checkIfPageEnabledInSitemap = (globalState, pageId) => {
  return (
    globalState &&
    globalState.sitemap &&
    globalState.sitemap.Items &&
    globalState.sitemap.Items.length > 0 &&
    checkIfPageExistsInSitemap(globalState.sitemap.Items, pageId)
  );
};

const checkIfPageExistsInSitemap = (sitemapItems, pageId) => {
  let result = false;
  sitemapItems.forEach((item) => {
    if (
      item.ItemType === SidebarItemType.Page &&
      item.Page &&
      item.Page.Id &&
      item.Page.Id === pageId
    ) {
      result = true;
    } else if (
      item.ItemType === SidebarItemType.Section &&
      item.Section &&
      item.Section.SectionItems &&
      item.Section.SectionItems.length > 0
    ) {
      item.Section.SectionItems.forEach((sectionItem) => {
        if (
          sectionItem.ItemType === SidebarItemType.Page &&
          sectionItem.Page &&
          sectionItem.Page.Id &&
          sectionItem.Page.Id === pageId
        ) {
          result = true;
        } else {
          if (
            sectionItem.ItemType === SidebarItemType.Section &&
            sectionItem.SubSection &&
            sectionItem.SubSection.Pages &&
            sectionItem.SubSection.Pages.length > 0
          ) {
            sectionItem.SubSection.Pages.forEach((page) => {
              if (page && page.Id && page.Id === pageId) {
                result = true;
              }
            });
          }
        }
      });
    }
  });

  return result;
};

const checkIfTwoArraysAreTheSame = (array1, array2) => {
  //If both arrays are null, then return true
  if (!array1 && !array2) {
    return true;
  }

  //If at least one of the two arrays are null or if their lengths don't match then return false
  if (!array1 || !array2 || array1.length !== array2.length) {
    return false;
  }

  //If at least one value in each arrays indexes don't match, then return false
  for (var i = 0; i < array1.length; ++i) {
    if (array1[i] !== array2[i]) {
      return false;
    }
  }

  //Otherwise they match
  return true;
};

const convertDateTimeToUTCTimeZoneWhileMaintainingDateTimeValue = (
  date
) => {
  if (date) {
    return moment(date).utc(true);
  }
  return null;
};

const createDateFromToday = function (days, months, years) {
  var date = new Date();
  date.setDate(date.getDate() + days);
  date.setMonth(date.getMonth() + months);
  date.setFullYear(date.getFullYear() + years);
  return date;
};

const createDate = function (days, months, years) {
  var date = new Date();
  date.setDate(days);
  date.setMonth(months);
  date.setFullYear(years);
  return date;
};

const createMarkup = function (markup) {
  return { __html: markup };
};

function disableLinkClicks(event) {
  event.preventDefault();
  event.stopPropagation();
}

const downloadAnnotation = async (id) => {
  const [serviceResponse] = await Promise.all([
    GetEntityRecordById(null, "annotation", id),
  ]);

  if (serviceResponse && serviceResponse.data) {
    const { documentbody, filename, mimetype } =
      serviceResponse.data.Fields;

    // Decode base64 string to binary data
    const binaryData = atob(documentbody);

    // Convert binary data to array buffer
    const arrayBuffer = new ArrayBuffer(binaryData.length);
    const uint8Array = new Uint8Array(arrayBuffer);
    for (let i = 0; i < binaryData.length; i++) {
      uint8Array[i] = binaryData.charCodeAt(i);
    }

    // Create blob from array buffer
    const blob = new Blob([uint8Array], { type: mimetype });

    // Create URL for the blob
    const url = URL.createObjectURL(blob);

    // Create link element and trigger download
    const link = document.createElement("a");
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();

    // Remove the link
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  }
};

//The FormDatePicker component displays the numerical day value for a date with a lowercase d
//Moment displays the numerical day value for a date with a uppercase D whereas lowercase d is for the worded day value.
//So if the field has a dateformat set to dd/MM/yyyy then for a date value of 16/11/2023
//moment would display it as Th/11/2023. This function corrects this into the correct format
function convertDayFormatForMoment(dateFormat) {
  if (dateFormat === "dd/MM/yyyy HH:mm:ss") {
    return "DD/MM/yyyy HH:mm:ss";
  }
  if (dateFormat === "dd/MM/yyyy HH:mm") {
    return "DD/MM/yyyy HH:mm";
  }
  if (dateFormat === "dd/MM/yyyy") {
    return "DD/MM/yyyy";
  }
  return dateFormat;
}

const getAuditLabel = (audit, includeDateTime) => {
  let auditType = "";

  switch (audit.Type) {
    case 0:
      auditType = "Created by ";
      break;
    case 5:
      auditType = "Activated by ";
      break;
    case 6:
      auditType = "Deactivated by ";
      break;
    case 1:
    default:
      auditType = "Updated by ";
      break;
  }

  let auditTime = includeDateTime
    ? `on ${moment(audit.CreatedOn).format(
        convertDayFormatForMoment("dd/MM/yyyy HH:mm:ss")
      )}`
    : "";

  return `${auditType}${audit.EmailAddress}${auditTime}`;
};

const getEntityNameAttribute = (entityName) => {
  switch (entityName) {
    case "annotation":
      return "subject";
    case "account":
    case "campaign":
    case "customeraddress":
      return "name";
    case "contact":
      return "fullname";
    case "g4_answergroup":
    case "g4_answergroupoption":
    case "g4_answeroption":
    case "g4_answeroptioncategory":
    case "g4_city":
    case "g4_configuration":
    case "g4_country":
    case "g4_group":
    case "g4_language":
    case "g4_question":
    case "g4_region":
      return "g4_name";
    case "g4b_booking":
      return "g4b_bookingreference";
    case "g4b_bookingproduct":
      return "g4b_protoproductidname";
    case "g4c_abtest":
    case "g4c_communication":
    case "g4c_communicationaudience":
    case "g4c_communicationoptions":
    case "g4c_communicationsavedlayout":
    case "g4c_subscriptiongroup":
    case "g4c_template":
    case "g4c_triggerflow":
      return "g4c_name";
    case "g4d_fund":
    case "g4d_charity":
      return "g4d_name";
    case "g4gdpr_contactaudit":
      return "g4gdpr_name";
    case "g4m_membership":
    case "g4m_membershipaccount":
    case "g4m_membershippaymentplan":
    case "g4m_membershippaymentplanschedule":
      return "g4m_name";
    case "list":
      return "listname";
    default:
      return "g4b_name";
  }
};

const getFieldDataType = (field) => {
  if (field === null || field === undefined) {
    return "";
  }

  switch (typeof field) {
    case "number":
      return "number";
    case "boolean":
      return "boolean";
    case "string":
      return "string";
    case "object":
      return Array.isArray(field) ? "array" : "object";
    default:
      return moment(field, moment.ISO_8601, true).isValid()
        ? "datetime"
        : "";
  }
};

const getImageBase64Src = (mimeType, base64String) => {
  return `data:${mimeType};base64,${base64String}`;
};

const getPageForEntity = (entityName) => {
  switch (entityName) {
    case "account":
      return "/account";
    case "contact":
      return "/contact";
    case "g4b_booking":
      return "/booking";
    case "g4b_bookingpayment":
      return "/booking-payment";
    case "g4b_coupon":
      return "/coupon";
    case "g4b_voucher":
      return "/voucher";
    case "g4m_membership":
      return "/membership";
    default:
      return "";
  }
};

const getLookupData = (lookupName, state) => {
  const lookupData =
    state && state.lookupOptions
      ? state.lookupOptions.find(
          (options) => options.name === lookupName
        )
      : null;

  return lookupData && lookupData.data && lookupData.data.length > 0
    ? lookupData.data
    : null;
};

function getOrdinalSuffix(n) {
  const s = ["th", "st", "nd", "rd"];
  const v = n % 100;
  return n + (s[(v - 20) % 10] || s[v] || s[0]);
}

function displayFieldValue(value, type = "", field, record, state) {
  if (field && field.displayFieldValue) {
    return field.displayFieldValue(value, record);
  }

  if (value === null || value === undefined) {
    return "";
  }

  const lookupData =
    state && state.lookupOptions && field && field.lookup
      ? state.lookupOptions.find(
          (options) => options.name === field.lookup.name
        )
      : null;

  const fieldType = type !== "" ? type : getFieldDataType(value);
  switch (fieldType) {
    case "int":
      return value.toLocaleString("en-GB", {
        useGrouping: "true",
      });
    case "number":
      return value.toLocaleString("en-GB", {
        useGrouping: "true",
        minimumFractionDigits: "2",
        maximumFractionDigits: "4",
      });
    case "money":
      return formatCurrency(value);
    case "bit":
    case "boolean":
      return field.inverted
        ? displayBoolean(!value, "No")
        : displayBoolean(value, "No");
    case "select":
    case "string":
    case "text":
      return value;
    case "object":
    case "picklist":
      //If record isn't in the form of {Name, Id, Fields} and is instead a value, then check for whether
      //a enum was provided and match the value to the enum value and return the enum key name
      return value !== undefined && String(value)
        ? value.Name !== undefined && String(value.Name)
          ? String(value.Name)
          : field && field.enum
          ? String(
              Object.keys(field.enum).some(
                (key) => String(field.enum[key]) === String(value)
              )
                ? Object.keys(field.enum).find(
                    (key) => String(field.enum[key]) === String(value)
                  )
                : ""
            )
          : ""
        : "";
    case "array":
      return value && value.length > 0 ? (
        <BadgeList
          backgroundStyle={BackgroundStyle.Info}
          items={
            value && lookupData?.data?.length > 0
              ? value
                  .map((v) => {
                    const entry = lookupData.data.find(
                      (l) => l.Key === v
                    );
                    return { text: entry?.Value ?? "" };
                  })
                  .sort((a, b) => a.text.localeCompare(b.text))
              : [{ text: value }]
          }
        />
      ) : (
        ""
      );
    case "statusbadge":
      return value !== undefined && field && field.enum ? (
        <StatusBadge enumList={field.enum} value={value} />
      ) : (
        ""
      );
    case "datetime":
      return field && field.dateFormat && value
        ? moment(value).format(
            convertDayFormatForMoment(field.dateFormat)
          )
        : value
        ? displayLocalDate(value)
        : "";
    default:
      return "";
  }
}

function displayLocalDate(date) {
  if (date !== 0 && date !== undefined) {
    return new Date(date).toLocaleDateString("en-GB", {
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
    });
  } else {
    return "";
  }
}

function displayBoolean(bool, defaultBool) {
  if (bool !== undefined) {
    return bool === true ? "Yes" : "No";
  } else {
    return defaultBool;
  }
}

function formatCurrency(value) {
  return currencyFormat.format(value);
}

function formatNumber(number) {
  return numberFormat.format(number);
}

const isSameDay = (date, date1) => {
  // compare two dates to see if they fall within the same calendar day
  date = moment(date).format("YYYY-MM-DD");
  date1 = moment(date1).format("YYYY-MM-DD");
  return moment(date).isSame(date1);
};

const isSameOrBeforeDate = (date, dateToCompare) => {
  // if either value is not set then return true
  if (!date || !dateToCompare) {
    return true;
  }
  // check both are date objects
  if (typeof date === "string") date = new Date(date);
  if (typeof dateToCompare === "string")
    dateToCompare = new Date(dateToCompare);

  // clone date and dateToCompare into clonedDate and clonedDateToCompare
  // and set them to be at midnight so that when we do the comparison,
  // the time isn't a factor. The cloning to done to make sure that
  // the original values do not get changed
  var clonedDate = new Date(date);
  clonedDate = new Date(clonedDate.setHours(0, 0, 0, 0));
  var clonedDateToCompare = new Date(dateToCompare);
  clonedDateToCompare = new Date(
    clonedDateToCompare.setHours(0, 0, 0, 0)
  );

  // compare the dates
  return clonedDate <= clonedDateToCompare;
};

const isSameOrLaterDate = (date, dateToCompare) => {
  // if either value is not set then return true
  if (!date || !dateToCompare) {
    return true;
  }
  // check both are date objects
  if (typeof date === "string") date = new Date(date);
  if (typeof dateToCompare === "string")
    dateToCompare = new Date(dateToCompare);

  // clone date and dateToCompare into clonedDate and clonedDateToCompare
  // and set them to be at midnight so that when we do the comparison,
  // the time isn't a factor. The cloning to done to make sure that
  // the original values do not get changed
  var clonedDate = new Date(date);
  clonedDate = new Date(clonedDate.setHours(0, 0, 0, 0));
  var clonedDateToCompare = new Date(dateToCompare);
  clonedDateToCompare = new Date(
    clonedDateToCompare.setHours(0, 0, 0, 0)
  );

  // compare the dates
  return clonedDate >= clonedDateToCompare;
};

const isLaterTime = (time, dateToCompare) => {
  // if either value is not set then return true
  if (!time || !dateToCompare) {
    return true;
  }
  // check both are date objects
  if (typeof time === "string") time = new Date(time);
  if (typeof dateToCompare === "string")
    dateToCompare = new Date(dateToCompare);
  // compare the date times
  return time.getTime() > dateToCompare.getTime();
};

const isValidEmail = (email) => {
  const emailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return emailPattern.test(email);
};

function getEntityNameForRegarding(regardingType) {
  switch (String(regardingType)) {
    case "1":
      return "g4b_booking";
    case "2":
      return "g4b_bookingpayment";
    case "3":
      return "contact";
    case "4":
    case "5":
      return "g4m_membership";
    case "6":
      return "account";
    case "7":
      return "g4b_coupon";
    case "8":
      return "g4b_voucher";
    default:
      return "";
  }
}

function getEntityNumberForRegarding(entityName) {
  switch (entityName) {
    case "g4b_booking":
      return 1;
    case "g4b_bookingpayment":
      return 2;
    case "contact":
      return 3;
    case "g4m_membership":
      return 4;
    //5 is used for Membership (Owner) but isn't needed for this as we intend to use this
    //for dynamic content
    case "account":
      return 6;
    case "g4b_coupon":
      return 7;
    case "g4b_voucher":
      return 8;
    default:
      return "";
  }
}

function getEnumKey(type, value) {
  return Object.keys(type).find(
    (key) => type[key] === parseInt(value, 10)
  );
}

function getReadOnlyValue(element, state, values, globalState) {
  const { dateFormat, getReadOnlyValue, textAreaHeight, type, name } =
    element;
  if (getReadOnlyValue) {
    return getReadOnlyValue(state);
  }

  const value = state[name];
  const emptyValue = "--";

  switch (type) {
    case "bit":
      return value ? "Yes" : "No";
    case "picklist":
      const entries = Object.entries(
        getEnum(element, state, values, globalState)
      );
      // try to match as an integer
      let match = entries.find(([_, val]) => val === parseInt(value));
      if (match) {
        return match[0];
      }
      // else try to match on just the value
      match = entries.find(([_, val]) => val === value);
      if (match) {
        return match[0];
      }
      return emptyValue;
    case "lookup":
      return value ? state[name + "name"] : emptyValue;
    case "datetime":
      return dateFormat && value
        ? moment(value).format(convertDayFormatForMoment(dateFormat))
        : value
        ? displayLocalDate(value)
        : emptyValue;
    case "richtexteditor":
      return <p dangerouslySetInnerHTML={createMarkup(value)} />;
    case "money":
      return value ? currencyFormat.format(value) : emptyValue;
    case "textarea":
      return (
        <TextArea
          disabled={true}
          height={textAreaHeight ? textAreaHeight : "60px"}
          value={value}
        />
      );
    default:
      return value || emptyValue;
  }
}

function isRegardingDifferentToListType(listType, regardingType) {
  const targetAtEntityNumber = String(listType) === "1" ? 6 : 3; //6 for account, 3 for contact
  return String(regardingType) !== String(targetAtEntityNumber);
}

function getRelatedEntitiesFromValues(values, entityName) {
  const records = values.relatedEntities
    ? values.relatedEntities.find((r) => r.entityName === entityName)
    : [];

  return records && records.entities ? records.entities : [];
}

const useClickOutside = (ref, handler) => {
  useEffect(() => {
    let startedInside = false;
    let startedWhenMounted = false;

    const listener = (event) => {
      // Do nothing if `mousedown` or `touchstart` started inside ref element
      if (startedInside || !startedWhenMounted) return;
      // Do nothing if clicking ref's element or descendent elements
      if (!ref.current || ref.current.contains(event.target)) return;

      handler(event);
    };

    const validateEventStart = (event) => {
      startedWhenMounted = ref.current;
      startedInside =
        ref.current && ref.current.contains(event.target);
    };

    document.addEventListener("mousedown", validateEventStart);
    document.addEventListener("touchstart", validateEventStart);
    document.addEventListener("click", listener);

    return () => {
      document.removeEventListener("mousedown", validateEventStart);
      document.removeEventListener("touchstart", validateEventStart);
      document.removeEventListener("click", listener);
    };
  }, [ref, handler]);
};

function simpleText(text, isBold = false) {
  if (isBold) {
    return (
      <strong>
        <span>{text}:</span>
      </strong>
    );
  }
  return <>{<span>{text}</span>}</>;
}

function useWindowResize(event) {
  const throttleTime = 200;

  const [windowSize, setWindowSize] = useState({
    width: window.innerWidth,
    height: window.innerHeight,
  });

  useEffect(() => {
    let timeoutId;

    // Handler to update window size when the window is resized
    function handleResize() {
      if (!timeoutId) {
        timeoutId = setTimeout(() => {
          setWindowSize({
            width: window.innerWidth,
            height: window.innerHeight,
          });
          timeoutId = null;
        }, throttleTime);
      }
    }

    // Add event listener to "resize" event
    window.addEventListener("resize", handleResize);

    // Add event listener to custom event, if provided
    if (event) {
      window.addEventListener("resize", event);
    }

    // Clean up the event listener and timeout when the component unmounts
    return () => {
      window.removeEventListener("resize", handleResize);

      if (event) {
        window.removeEventListener("resize", event);
      }

      if (timeoutId) {
        clearTimeout(timeoutId);
      }
    };
  }, [event, throttleTime]);

  return windowSize;
}

function stringReplaceWithArgs(formtString, args) {
  return formtString.replace(/{(\d+)}/g, function (match, number) {
    return typeof args[number] != "undefined" ? args[number] : match;
  });
}

export {
  checkDateTimeIsAfterToday,
  checkIfTwoArraysAreTheSame,
  checkIfDynamicContentEnabled,
  checkIfEmailMarketingEnabled,
  checkIfFixturesAndSeriesEnabled,
  checkIfPageEnabledInSitemap,
  checkIfWarehouseCommunicationAudienceEnabled,
  convertDateTimeToUTCTimeZoneWhileMaintainingDateTimeValue,
  convertDayFormatForMoment,
  createDate,
  createDateFromToday,
  createMarkup,
  disableLinkClicks,
  displayFieldValue,
  displayLocalDate,
  downloadAnnotation,
  formatCurrency,
  formatNumber,
  isSameDay,
  getAuditLabel,
  getEntityNameAttribute,
  getEntityNameForRegarding,
  getEntityNumberForRegarding,
  getEnumKey,
  getImageBase64Src,
  getLookupData,
  getOrdinalSuffix,
  getPageForEntity,
  getReadOnlyValue,
  getRelatedEntitiesFromValues,
  isLaterTime,
  isRegardingDifferentToListType,
  isSameOrBeforeDate,
  isSameOrLaterDate,
  isValidEmail,
  priceRegex,
  simpleText,
  stringReplaceWithArgs,
  useClickOutside,
  useWindowResize,
};
